import * as THREE from "three";
import { CSS2DObject, CSS2DRenderer } from "three/addons/renderers/CSS2DRenderer.js";
import type { MeasurementType } from "../types.ts";
import { convertValue } from "../utils/units.ts";

export { CSS2DRenderer };

interface LabelEntry {
  object: CSS2DObject;
  line?: THREE.Line;
  points?: THREE.Points;
  group: THREE.Group;
  // For live re-formatting when the user toggles units.
  type?: MeasurementType;
  metricValue?: number;
  customLabel?: string | null;
}

export class AnnotationManager {
  private scene: THREE.Scene;
  private entries: Map<string, LabelEntry> = new Map();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  /** Add a completed measurement annotation. Returns the annotation id. */
  addMeasurement(
    id: string,
    type: MeasurementType,
    points: THREE.Vector3[],
    value: number,
    unit: string,
    label?: string | null,
  ): void {
    this.remove(id);

    const group = new THREE.Group();
    group.name = `annotation_${id}`;

    // ── Label div ──────────────────────────────────────────────────────────
    const div = document.createElement("div");
    div.className = "measure-label";
    const displayText = label
      ? `${label}\n${_formatValue(value, unit, type)}`
      : _formatValue(value, unit, type);
    div.textContent = displayText;

    const labelObj = new CSS2DObject(div);
    labelObj.position.copy(_midpoint(points));
    group.add(labelObj);

    // ── Geometry ───────────────────────────────────────────────────────────
    if (points.length >= 2) {
      const lineGeo = new THREE.BufferGeometry().setFromPoints(
        type === "area" ? [...points, points[0]] : points,
      );
      const lineMat = new THREE.LineBasicMaterial({
        color: _color(type),
        linewidth: 2,
        depthTest: false,
      });
      const line = new THREE.Line(lineGeo, lineMat);
      line.renderOrder = 999;
      group.add(line);
    }

    // Dot markers at each point
    const dotGeo = new THREE.BufferGeometry().setFromPoints(points);
    const dotMat = new THREE.PointsMaterial({
      color: _color(type),
      size: 0.04,
      depthTest: false,
      sizeAttenuation: true,
    });
    const dots = new THREE.Points(dotGeo, dotMat);
    dots.renderOrder = 999;
    group.add(dots);

    this.scene.add(group);
    this.entries.set(id, {
      object: labelObj,
      group,
      type,
      metricValue: value,
      customLabel: label ?? null,
    });
  }

  /** Re-render stored labels after a unit-system change. */
  refreshLabels(): void {
    for (const entry of this.entries.values()) {
      if (entry.type == null || entry.metricValue == null) continue;
      const formatted = _formatValue(entry.metricValue, "", entry.type);
      entry.object.element.textContent = entry.customLabel
        ? `${entry.customLabel}\n${formatted}`
        : formatted;
    }
  }

  /** Add a temporary preview label (replaced every frame). */
  setPreview(points: THREE.Vector3[], type: MeasurementType): void {
    this.remove("__preview__");
    if (points.length === 0) return;

    const group = new THREE.Group();
    const div = document.createElement("div");
    div.className = "measure-label measure-label--preview";
    div.textContent = points.length === 1 ? "Click next point" : _computeLabel(type, points);
    const obj = new CSS2DObject(div);
    obj.position.copy(_midpoint(points));
    group.add(obj);

    if (points.length >= 2) {
      const geo = new THREE.BufferGeometry().setFromPoints(
        type === "area" ? [...points, points[0]] : points,
      );
      const mat = new THREE.LineDashedMaterial({
        color: 0x4a9eff,
        dashSize: 0.05,
        gapSize: 0.03,
        depthTest: false,
      });
      const line = new THREE.Line(geo, mat);
      line.computeLineDistances();
      line.renderOrder = 999;
      group.add(line);
    }

    const dotGeo = new THREE.BufferGeometry().setFromPoints(points);
    const dotMat = new THREE.PointsMaterial({ color: 0x4a9eff, size: 0.04, depthTest: false });
    group.add(new THREE.Points(dotGeo, dotMat));

    this.scene.add(group);
    this.entries.set("__preview__", { object: obj, group });
  }

  clearPreview(): void {
    this.remove("__preview__");
  }

  remove(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    entry.group.traverse((obj) => {
      if (obj instanceof CSS2DObject) {
        obj.element.remove();
      }
      if (obj instanceof THREE.Mesh || obj instanceof THREE.Line || obj instanceof THREE.Points) {
        obj.geometry.dispose();
        if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
        else (obj.material as THREE.Material).dispose();
      }
    });
    this.scene.remove(entry.group);
    this.entries.delete(id);
  }

  /** Briefly highlight a measurement annotation. */
  highlight(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    const div = entry.object.element;
    div.classList.add("measure-label--highlight");
    setTimeout(() => div.classList.remove("measure-label--highlight"), 2000);
  }

  /** Get the midpoint world position of a measurement. */
  getWorldPosition(id: string): THREE.Vector3 | null {
    const entry = this.entries.get(id);
    if (!entry) return null;
    const pos = new THREE.Vector3();
    entry.object.getWorldPosition(pos);
    return pos;
  }

  setVisible(visible: boolean): void {
    for (const entry of this.entries.values()) {
      if (entry.group.name === "annotation___preview__") continue;
      entry.group.visible = visible;
    }
  }

  clear(): void {
    for (const id of this.entries.keys()) this.remove(id);
  }

  dispose(): void {
    this.clear();
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function _midpoint(pts: THREE.Vector3[]): THREE.Vector3 {
  const mid = new THREE.Vector3();
  pts.forEach((p) => mid.add(p));
  return mid.divideScalar(pts.length);
}

function _color(type: MeasurementType): number {
  return { distance: 0x4aff6a, area: 0xffaa4a, height: 0xff6a4a, point: 0xffffff }[type];
}

function _formatValue(value: number, _unit: string, type: MeasurementType): string {
  const cv = convertValue(value, type);
  return `${cv.value.toFixed(2)} ${cv.unit}`;
}

function _computeLabel(type: MeasurementType, pts: THREE.Vector3[]): string {
  if (pts.length < 2) return "";
  let metricVal = 0;
  if (type === "distance") metricVal = pts[0].distanceTo(pts[1]);
  else if (type === "height") metricVal = Math.abs(pts[1].y - pts[0].y);
  else if (type === "area" && pts.length >= 3) metricVal = _polygonArea(pts);
  else return "";
  const { value, unit } = convertValue(metricVal, type);
  return `${value.toFixed(2)} ${unit}`;
}

function _polygonArea(pts: THREE.Vector3[]): number {
  // Shoelace on XZ plane
  let area = 0;
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % n];
    area += a.x * b.z - b.x * a.z;
  }
  return Math.abs(area) / 2;
}

/**
 * Compute raw metric measurement result.
 * Always returns meters / m² — conversion to display units happens at render time.
 */
export function computeMeasurementResult(
  type: MeasurementType,
  pts: THREE.Vector3[],
): { value: number; unit: string } {
  switch (type) {
    case "distance":
      return { value: pts[0].distanceTo(pts[1]), unit: "m" };
    case "height":
      return { value: Math.abs(pts[1].y - pts[0].y), unit: "m" };
    case "area":
      return { value: _polygonArea(pts), unit: "m²" };
    case "point":
      return { value: 0, unit: "" };
  }
}
